// TestQuestionAnswersModal.jsx
import React, { useMemo } from "react";
import "./TestQuestionAnswersModal.scss";

/**
 * @param {Object} props
 * @param {number|string} props.testIndex
 * @param {Array} props.questions - otázky pro daný testIndex (ze sortingTestQuestions)
 * @param {Object} props.answerMap - { [questionId]: {value: string|Object} }
 */
const TestQuestionAnswersModal = ({ testIndex, questions = [], answerMap = {} }) => {
    const groups = useMemo(() => {
        const byGroup = new Map();
        for (const q of questions) {
            const g = q.group ?? q._id;
            if (!byGroup.has(g)) byGroup.set(g, []);
            byGroup.get(g).push(q);
        }
        return Array.from(byGroup.entries()).map(([groupKey, list]) => ({ groupKey, list }));
    }, [questions]);

    const getUserAnswer = (q) => answerMap?.[q._id]?.value;

    const renderOpen = (q) => {
        const val = getUserAnswer(q); // {0: "...", 1: "..."}
        const parts = String(q.question || "").split("%s");
        if (!val || typeof val !== "object") {
            return <span className="answer not-answered">— nezodpovězeno —</span>;
        }
        const nodes = [];
        for (let i = 0; i < parts.length; i++) {
            nodes.push(<span key={`p-${i}`}>{parts[i]}</span>);
            if (i < parts.length - 1) {
                const fill = (val?.[i] ?? "").toString();
                nodes.push(
                    <span key={`ans-${i}`} className={`open-pill ${fill.trim() ? "filled" : "empty"}`}>
            {fill || "…"}
          </span>
                );
            }
        }
        return <span className="answer open">{nodes}</span>;
    };

    const renderAudio = (q) => {
        const val = getUserAnswer(q);
        const has = Boolean(val && (typeof val === "string" ? val.trim() : true));
        return (
            <span className={`answer audio ${has ? "exists" : "not-answered"}`}>
        {has ? "odpověď nahrána" : "— nezodpovězeno —"}
      </span>
        );
    };

    return (
        <div className="TestQuestionAnswersModal">
            <h3>Odpovědi uživatele – Test #{testIndex}</h3>

            {groups.map(({ groupKey, list }, gi) => (
                <div className="group" key={`g-${groupKey}-${gi}`}>
                    {list.map((q, qi) => {
                        const userVal = getUserAnswer(q);

                        return (
                            <div className="qrow" key={q._id}>
                                <div className="qhead">
                                    <div className="qnum">{gi + 1}.{qi + 1}</div>
                                </div>

                                {/* zobrazujeme rovnou jen otázku (bez instrukcí) */}
                                <div className="qtext">
                                    {q.type === 1 ? q.question.replaceAll("%s", "____") : q.question}
                                </div>

                                {/* odpověď uživatele + seznam možností (bez tlačítka) */}
                                <div className="qanswer">
                                    {q.type === 0 && (
                                        <ul className="options">
                                            {q.answerList?.map(opt => {
                                                const isSelected = userVal === opt._id;
                                                const isCorrect = !!opt.evaluation;
                                                return (
                                                    <li
                                                        key={opt._id}
                                                        className={[
                                                            "opt",
                                                            isCorrect ? "correct" : "wrong",
                                                            isSelected ? "selected" : ""
                                                        ].join(" ").trim()}
                                                        aria-current={isSelected ? "true" : "false"}
                                                    >
                                                        <span className="opt-bullet" />
                                                        <span className="opt-text">{opt.value}</span>
                                                        {isSelected && (
                                                            <small className={`badge ${isCorrect ? "ok" : "bad"}`}>
                                                                {isCorrect ? "správně" : "špatně"}
                                                            </small>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}

                                    {q.type === 1 && renderOpen(q)}
                                    {q.type === 2 && renderAudio(q)}

                                    {/* pokud u výběrové otázky nebylo nic vybráno */}
                                    {q.type === 0 && !q.answerList?.some(a => a._id === userVal) && (
                                        <div className="not-answered mt-4">— nezodpovězeno —</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default TestQuestionAnswersModal;
